/**
 * TanStack Query hook and key factory for the caller's own access.
 */

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/contexts/AuthContext"
import { accessApi } from "@/services/accessApi"
import { type Experience, experienceOf, holdsTenantCapability, pagesFor } from "@/utils/access"

const accessKeys = {
  all: ["access"] as const,
  mine: (principalId: string) => [...accessKeys.all, "mine", principalId] as const,
}

/**
 * The caller's access, keyed by user so nobody sees a previous user's cached access. Disabled while
 * signed out.
 */
export function useMyAccess() {
  const { user } = useAuth()
  const signedIn = user?.isAuthenticated ?? false
  const query = useQuery({
    queryKey: accessKeys.mine(user?.principalId ?? ""),
    queryFn: accessApi.getMyAccess,
    enabled: signedIn,
    staleTime: 5 * 60_000,
  })
  const access = signedIn ? query.data : undefined
  const experience: Experience | null = experienceOf(access)
  return {
    access,
    experience,
    isInternal: experience === "internal",
    isExternal: experience === "external",
    pages: pagesFor(access),
    holdsTenantCapability: (capability: string) => holdsTenantCapability(access, capability),
    isLoading: signedIn && query.isPending,
    error: query.error,
  }
}
